import { useState } from 'react';
import type { SeriesGroup } from '../../types/manga';
import MangaCard from './MangaCard';
import { useAutoSetThumbnail } from '../../hooks/useAutoSetThumbnail';
import { getTagLabel, getTagColorClass } from '../../utils/tagUtils';

const EXPANDED_KEY = 'manga-series-expanded';

function getExpandedSet(): Set<string> {
  try {
    const raw = sessionStorage.getItem(EXPANDED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function saveExpandedSet(set: Set<string>) {
  try {
    sessionStorage.setItem(EXPANDED_KEY, JSON.stringify([...set]));
  } catch {}
}

interface SeriesCardProps {
  series: SeriesGroup;
  onVolumeClick: (id: string) => void;
  isSelectMode?: boolean;
  selectedIds?: Set<string>;
  onSelect?: (ids: string[]) => void;
}

export default function SeriesCard({ series, onVolumeClick, isSelectMode, selectedIds, onSelect }: SeriesCardProps) {
  const { baseTitle, author, volumeCount, volumes, representativeItem, seriesKey } = series;
  const [isExpanded, setIsExpanded] = useState(() => getExpandedSet().has(seriesKey));
  const { ref: thumbRef, displayUrl } = useAutoSetThumbnail(representativeItem);
  const thumbnailUrl = displayUrl ?? representativeItem.thumbnail_url;

  // 全巻のタグを重複なしでまとめる
  const allTags = volumes.flatMap((v) => v.tags ?? []);
  const uniqueTags = [...new Map(allTags.map((t) => [t.id, t])).values()];

  // シリーズ全体の最高レーティング
  const maxRating = Math.max(...volumes.map((v) => v.rating));

  const allVolumeIds = volumes.map((v) => v.id);
  const isAllSelected = allVolumeIds.length > 0 && allVolumeIds.every((id) => selectedIds?.has(id));

  const handleHeaderClick = () => {
    if (isSelectMode) {
      onSelect?.(allVolumeIds);
      return;
    }
    setIsExpanded((prev) => {
      const next = !prev;
      const set = getExpandedSet();
      if (next) set.add(seriesKey); else set.delete(seriesKey);
      saveExpandedSet(set);
      return next;
    });
  };

  return (
    <div className={`bg-yellow-50 rounded-lg shadow hover:shadow-lg transition overflow-hidden${isAllSelected ? ' ring-2 ring-red-500' : ''} ${isExpanded ? 'md:col-span-4 lg:col-span-6' : ''}`}>
      {/* シリーズサマリー行（常に表示） */}
      <div
        onClick={handleHeaderClick}
        className="cursor-pointer flex md:block"
      >
        {/* サムネイル: 折りたたみ時のみ表示 */}
        {!isExpanded && (
          <div ref={thumbRef} className="relative w-20 flex-shrink-0 bg-gray-200 flex items-center justify-center md:w-auto md:aspect-[2/3]">
            {isSelectMode && (
              <div className="absolute left-1 top-1 z-20">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={() => onSelect?.(allVolumeIds)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 accent-red-600"
                />
              </div>
            )}
            {/* 巻数バッジ */}
            <div className="absolute right-1 top-1 rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white md:right-2 md:top-2 md:text-[10px] z-10">
              {volumeCount} 巻
            </div>
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={baseTitle}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <span className="text-gray-400 text-xs md:text-sm">No Cover</span>
            )}
          </div>
        )}
        {/* テキスト情報 */}
        <div className="p-3 flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <div className="min-w-0 flex-1">
              {isExpanded && (
                <span className="inline-flex items-center rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white mr-2">
                  {volumeCount} 巻
                </span>
              )}
              <h3 className="font-bold text-sm truncate inline" title={baseTitle}>
                {baseTitle}
              </h3>
            </div>
            {/* 展開/折りたたみアイコン */}
            <span className="text-gray-400 text-xs flex-shrink-0 mt-0.5 ml-1">
              {isExpanded ? '▲' : '▼'}
            </span>
          </div>
          <p className="text-xs text-gray-600 truncate mt-0.5" title={author}>
            {author}
          </p>
          {uniqueTags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {uniqueTags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${getTagColorClass(tag.name)}`}
                  title={getTagLabel(tag.name)}
                >
                  {getTagLabel(tag.name)}
                </span>
              ))}
              {uniqueTags.length > 3 && (
                <span className="text-[10px] text-gray-400">
                  +{uniqueTags.length - 3}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 mt-2">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className={`text-sm ${i < maxRating ? 'text-yellow-500' : 'text-gray-300'}`}
              >
                ★
              </span>
            ))}
          </div>
          {!isExpanded && (
            <div className="text-xs text-gray-500 mt-1">{volumeCount} 巻シリーズ</div>
          )}
        </div>
      </div>

      {/* 展開時: 個別巻のカードグリッド */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-3 bg-gray-50">
          <div className="text-xs text-gray-500 mb-2 font-medium">全 {volumeCount} 巻</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {volumes.map((vol) => {
              // タイトルからベースタイトル部分を削除
              const displayTitle = vol.title.startsWith(baseTitle)
                ? vol.title.substring(baseTitle.length).trim()
                : vol.title;
              const displayItem = { ...vol, title: displayTitle };
              return (
                <MangaCard
                  key={vol.id}
                  item={displayItem}
                  onClick={() => onVolumeClick(vol.id)}
                  isSelectMode={isSelectMode}
                  isSelected={selectedIds?.has(vol.id)}
                  onSelect={(id) => onSelect?.([id])}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
